import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Download, LogIn, LogOut } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { reportsApi } from '@/api/endpoints/reports';
import { geofencesApi } from '@/api/endpoints/geofences';
import { useFleetStore } from '@/stores/fleet';
import { formatDateTime } from '@/lib/utils';
import { exportCsv } from './_shared';

function daysAgoRange(days: number): { from: string; to: string } {
  const now = new Date();
  const past = new Date(now.getTime() - days * 86400000);
  const iso = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { from: iso(past), to: iso(now) };
}

export function GeofenceEventsReportPage() {
  const init = daysAgoRange(7);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [run, setRun] = useState(0);

  const devices = useFleetStore((s) => s.devices);
  const deviceIds = useMemo(() => Array.from(devices.keys()), [devices]);

  const geofencesQ = useQuery({ queryKey: ['geofences'], queryFn: () => geofencesApi.list() });
  const geoName = useMemo(() => {
    const m = new Map<number, string>();
    for (const g of geofencesQ.data ?? []) m.set(g.id, g.name);
    return m;
  }, [geofencesQ.data]);

  const eventsQ = useQuery({
    queryKey: ['geofence-events', from, to, run],
    enabled: run > 0 && deviceIds.length > 0,
    queryFn: () =>
      reportsApi.events({
        deviceId: deviceIds,
        type: ['geofenceEnter', 'geofenceExit'],
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      }),
  });

  const rows = useMemo(() => {
    const list = (eventsQ.data ?? []).map((e) => ({
      time: e.serverTime,
      device: (e.deviceId != null && devices.get(e.deviceId)?.name) || (e.deviceId ? `#${e.deviceId}` : '—'),
      geofence: (e.geofenceId != null && geoName.get(e.geofenceId)) || (e.geofenceId ? `#${e.geofenceId}` : '—'),
      entrada: e.type === 'geofenceEnter',
    }));
    return list.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }, [eventsQ.data, devices, geoName]);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <ShieldCheck className="h-5 w-5 text-primary" /> Geocercas — Entradas e Saídas
        </h1>
        <p className="text-sm text-muted-foreground">Histórico de entradas e saídas de geocercas das embarcações no período.</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <label className="space-y-1">
            <span className="block text-xs font-semibold text-muted-foreground">De</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="block text-xs font-semibold text-muted-foreground">Até</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          </label>
          <Button onClick={() => setRun((x) => x + 1)} disabled={eventsQ.isFetching}>
            {eventsQ.isFetching ? 'Buscando…' : 'Gerar'}
          </Button>
          {rows.length > 0 && (
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => exportCsv(`geocercas-eventos-${from}_a_${to}.csv`,
                ['data_hora', 'embarcacao', 'geocerca', 'evento'],
                rows.map((r) => [formatDateTime(r.time), r.device, r.geofence, r.entrada ? 'Entrada' : 'Saída']))}
            >
              <Download className="h-4 w-4" /> CSV
            </Button>
          )}
        </CardContent>
      </Card>

      {run === 0 ? (
        <p className="text-sm text-muted-foreground">Escolha o período e clique em <strong>Gerar</strong>.</p>
      ) : eventsQ.isFetching ? (
        <p className="text-sm text-muted-foreground">Buscando eventos de geocerca…</p>
      ) : eventsQ.error ? (
        <p className="text-sm text-destructive">Erro ao carregar (faça login / permissão).</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma entrada/saída de geocerca no período.</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Data/hora</th>
                    <th className="px-3 py-2">Embarcação</th>
                    <th className="px-3 py-2">Geocerca</th>
                    <th className="px-3 py-2">Evento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{formatDateTime(r.time)}</td>
                      <td className="px-3 py-2 font-medium">{r.device}</td>
                      <td className="px-3 py-2">{r.geofence}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.entrada ? 'bg-status-running/10 text-status-running' : 'bg-status-ready/15 text-status-ready'}`}>
                          {r.entrada ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
                          {r.entrada ? 'Entrada' : 'Saída'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-border p-2 text-center text-[11px] text-muted-foreground">{rows.length} eventos</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
